// Diseño de PDF "limpio" (18-oct-2026, Idea 1): sin cajas, un solo tipo de letra en pocos tamaños,
// separadores finos, el total como un renglón más y el lugar para las fotos.
// Todo lo comparten el trabajo, el presupuesto (uno o muchos equipos) y la venta.
import autoTable from 'jspdf-autotable';
import { C, LOGO_URL, M, CONTENT_W, PAGE_H, getEmpresa } from '../theme.js';
import { pesos } from '../pdfShared.js';

// Tamaños (pt) y colores: no hay otros
export const F = { titulo: 12, nombre: 11, texto: 8.5, chico: 7, rotulo: 6.5 };
export const K = { tinta: [34, 34, 34], gris: [100, 100, 106], rotulo: [138, 138, 144], linea: [226, 226, 230], fuerte: [34, 34, 34], rojo: C.red };
export const PIE = 22;                 // reservado abajo para el pie de página
export const LIMITE = PAGE_H - PIE;     // hasta dónde se puede dibujar
const ANCHO = 210;

export function texto(doc, s, x, y, { tam = F.texto, color = K.tinta, negrita = false, alinear = 'left', ancho = null } = {}) {
    doc.setFontSize(tam);
    doc.setFont(undefined, negrita ? 'bold' : 'normal');
    doc.setTextColor(...color);
    const t = ancho ? doc.splitTextToSize(String(s ?? ''), ancho) : String(s ?? '');
    doc.text(t, x, y, { align: alinear });
    return Array.isArray(t) ? t.length : 1;
}
export const rotulo = (doc, s, x, y, opts = {}) => texto(doc, String(s).toUpperCase(), x, y, { tam: F.rotulo, color: K.rotulo, negrita: true, ...opts });
export const plata = (v) => `$ ${pesos(Math.round(Number(v || 0)))}`;
export function linea(doc, y, { color = K.linea, grosor = 0.2, x1 = M, x2 = null } = {}) {
    if (x2 == null) x2 = doc.internal.pageSize.getWidth() - M;   // también en hojas apaisadas
    doc.setDrawColor(...color);
    doc.setLineWidth(grosor);
    doc.line(x1, y, x2, y);
}

// ── Cabecera: el logo (que ya dice el nombre) y a la derecha qué documento es ──────────────
// ctx = { titulo, sub }   sub = "N° … · fecha · Técnico: …"
export function cabecera(doc, ctx, { mini = false } = {}) {
    // 18-oct-2026: la marca con más peso. Logo más grande y el título centrado a su altura,
    // así no queda flotando arriba a la izquierda.
    const altoLogo = mini ? 11 : 19;
    const yLogo = mini ? 7 : 8;
    try { doc.addImage(LOGO_URL, 'PNG', M, yLogo, altoLogo * (700 / 300), altoLogo); } catch { /* sin logo */ }
    const centro = yLogo + altoLogo / 2;
    const der = doc.internal.pageSize.getWidth() - M;   // también en hojas apaisadas (liquidación)
    texto(doc, ctx.titulo, der, centro - (mini ? 0.3 : 0.6), { tam: mini ? F.nombre : 14, negrita: true, alinear: 'right' });
    texto(doc, ctx.sub, der, centro + (mini ? 4.2 : 5), { tam: F.chico, color: K.gris, alinear: 'right' });
    const yLinea = yLogo + altoLogo + (mini ? 3 : 4);
    linea(doc, yLinea, { color: K.rojo, grosor: 0.6 });
    return yLinea + 7;
}

// Hoja nueva con la cabecera chica
export function nuevaHoja(doc, ctx) {
    doc.addPage();
    return cabecera(doc, ctx, { mini: true });
}
export function espacio(doc, ctx, y, necesito) {
    return y + necesito > LIMITE ? nuevaHoja(doc, ctx) : y;
}

// ── Datos: dos columnas (cliente | equipo / alcance / condiciones) ───────────────────────────
// col = { rotulo, nombre, lineas: [] }
export function datos(doc, y, izq, der = null) {
    const xDer = M + CONTENT_W * 0.6;
    const col = (c, x, ancho) => {
        if (!c) return y;
        rotulo(doc, c.rotulo, x, y);
        let yy = y + 5.5;
        if (c.nombre) { yy += (texto(doc, c.nombre, x, yy, { tam: F.nombre, negrita: true, ancho }) - 1) * 4.6; yy += 5; }
        (c.lineas || []).filter(Boolean).forEach(l => {
            const n = texto(doc, l, x, yy, { tam: F.texto, color: K.gris, ancho });
            yy += n * 3.9;
        });
        return yy;
    };
    const a = col(izq, M, CONTENT_W * 0.56);
    const b = col(der, xDer, CONTENT_W * 0.4);
    return Math.max(a, b) + 4;
}

// Párrafo con rótulo (lo que se hizo, observaciones, condiciones)
export function parrafo(doc, ctx, y, titulo, cuerpo, { maxRenglones = 6 } = {}) {
    const limpio = String(cuerpo || '').replace(/[\r\n]+/g, ' ').trim();
    if (!limpio) return y;
    doc.setFontSize(F.texto);
    const renglones = doc.splitTextToSize(limpio, CONTENT_W).slice(0, maxRenglones);
    y = espacio(doc, ctx, y, 6 + renglones.length * 3.9);
    rotulo(doc, titulo, M, y);
    texto(doc, renglones, M, y + 5);
    return y + 5 + renglones.length * 3.9 + 3;
}

// ── Tabla con el mismo estilo que el resto (sin el encabezado negro) ─────────────────────────
// columnas: [{ titulo, ancho?, derecha? }]   filas: [[...]]   miniaturas: [{data,format,w,h}|null] por fila
export function tabla(doc, ctx, y, columnas, filas, { miniaturas = null, altoMiniatura = 12, negritaCol = null, tamCuerpo = F.texto } = {}) {
    const conMini = Array.isArray(miniaturas) && miniaturas.some(Boolean);
    const cols = conMini ? [{ titulo: '', ancho: altoMiniatura + 3 }, ...columnas] : columnas;
    const cuerpo = conMini ? filas.map(f => ['', ...f]) : filas;
    const columnStyles = {};
    cols.forEach((c, i) => {
        columnStyles[i] = { ...(c.ancho ? { cellWidth: c.ancho } : {}), ...(c.derecha ? { halign: 'right' } : {}) };
        if (negritaCol != null && i === negritaCol + (conMini ? 1 : 0)) columnStyles[i].fontStyle = 'bold';
    });
    autoTable(doc, {
        startY: y,
        head: [cols.map(c => c.titulo.toUpperCase())],
        body: cuerpo,
        theme: 'plain',
        margin: { left: M, right: M, top: 30, bottom: PIE + 2 },
        styles: { font: 'helvetica', fontSize: tamCuerpo, textColor: K.tinta, cellPadding: { top: 2.1, bottom: 2.1, left: 0, right: 2 }, valign: 'middle', overflow: 'linebreak' },
        headStyles: { fontSize: F.rotulo, fontStyle: 'bold', textColor: K.rotulo, cellPadding: { top: 1.5, bottom: 1.8, left: 0, right: 2 },
            lineColor: K.tinta, lineWidth: { bottom: 0.3 } },
        bodyStyles: { lineColor: K.linea, lineWidth: { bottom: 0.2 }, ...(conMini ? { minCellHeight: altoMiniatura + 3 } : {}) },
        columnStyles,
        didParseCell: (d) => {
            // El título de las columnas numéricas también a la derecha
            if (d.section === 'head' && cols[d.column.index]?.derecha) d.cell.styles.halign = 'right';
            // Celdas con salto de línea: el primer renglón en negrita (nombre del producto) lo hace drawCell
        },
        didDrawCell: (d) => {
            if (!conMini || d.section !== 'body' || d.column.index !== 0) return;
            const img = miniaturas[d.row.index];
            const lado = altoMiniatura;
            doc.setFillColor(238, 238, 241);
            doc.roundedRect(d.cell.x, d.cell.y + (d.cell.height - lado) / 2, lado, lado, 1, 1, 'F');
            if (img) {
                const r = Math.min(lado / (img.w || lado), lado / (img.h || lado));
                const w = (img.w || lado) * r, h = (img.h || lado) * r;
                try { doc.addImage(img.data, img.format, d.cell.x + (lado - w) / 2, d.cell.y + (d.cell.height - h) / 2, w, h); } catch { /* foto rota */ }
            }
        },
        didDrawPage: (d) => { if (d.pageNumber > 1) cabecera(doc, ctx, { mini: true }); },
    });
    return doc.lastAutoTable.finalY;
}

// ── Totales con el IVA según el cliente (18-oct-2026) ────────────────────────────────────────
// Los precios de la app ya incluyen IVA (21%).
// · Responsable Inscripto y Monotributo (factura A): renglones Neto + IVA 21% + Total.
// · Consumidor final, exento, organismos (factura B): Total con IVA incluido + "IVA contenido".
// · Presupuesto: además, cuánto queda pagando en efectivo (menos a un RI, que necesita la factura).
// · Trabajo ya cobrado en efectivo: el descuento aplicado como renglón, sin IVA (no se factura).
const DISCRIMINA = ['RESPONSABLE_INSCRIPTO', 'MONOTRIBUTO'];
export const IVA = 21;

export function totales(doc, ctx, y, {
    subtotal, descuentos = [], total, condicionIva = null, esPresupuesto = false,
    pctEfectivo = 0, cobradoEfectivo = false, montoCobrado = null, notas = [], etiquetaTotal = 'Total',
}) {
    const cond = String(condicionIva || '').toUpperCase();
    const filas = [];   // [etiqueta, monto, { fuerte, rojo }]
    const hayDesc = descuentos.some(d => Number(d.monto) > 0);
    let final = Number(total || 0);

    if (cobradoEfectivo) {
        const cobrado = montoCobrado != null && Number(montoCobrado) > 0 ? Number(montoCobrado) : final;
        const descEf = Math.max(0, final - cobrado);
        if (hayDesc || descEf > 0) filas.push(['Subtotal', subtotal]);
        descuentos.filter(d => Number(d.monto) > 0).forEach(d => filas.push([d.label, -d.monto]));
        if (descEf > 0) filas.push([`Descuento pago en efectivo${pctEfectivo ? ` ${pctEfectivo}%` : ''}`, -descEf]);
        filas.push(['Total pagado en efectivo', cobrado, { fuerte: true }]);
        final = cobrado;
    } else {
        if (hayDesc) {
            filas.push(['Subtotal', subtotal]);
            descuentos.filter(d => Number(d.monto) > 0).forEach(d => filas.push([d.label, -d.monto]));
        }
        const neto = final / (1 + IVA / 100);
        if (DISCRIMINA.includes(cond)) {
            filas.push(['Neto', neto]);
            filas.push([`IVA ${IVA}%`, final - neto]);
        }
        const conEfectivo = esPresupuesto && pctEfectivo > 0 && cond !== 'RESPONSABLE_INSCRIPTO';
        filas.push([conEfectivo ? 'Total con factura (IVA incluido)' : etiquetaTotal, final, { fuerte: true }]);
        if (conEfectivo) filas.push([`Pagando en efectivo (${pctEfectivo}% de descuento)`, final * (1 - pctEfectivo / 100), { efectivo: true }]);
        if (!DISCRIMINA.includes(cond)) notas = [`IVA contenido (${IVA}%): ${plata(final - neto)}`, ...notas];
    }

    // 18-oct-2026: el total es lo único destacado de la hoja: recuadro suave, monto grande, y la
    // opción en efectivo adentro, como segunda opción clara. Lo de arriba (subtotal, descuento,
    // neto, IVA) va en renglones chicos.
    const previas = filas.filter(([, , op = {}]) => !op.fuerte && !op.efectivo);
    const filaTotal = filas.find(([, , op = {}]) => op.fuerte);
    const efectivo = filas.find(([, , op = {}]) => op.efectivo);
    const altoCaja = efectivo ? 19 : 12;
    y = espacio(doc, ctx, y, previas.length * 5.5 + altoCaja + 14);
    y += 2;
    previas.forEach(([lab, monto]) => {
        texto(doc, lab, ANCHO - M - 48, y + 3.5, { tam: F.texto, color: K.gris, alinear: 'right' });
        texto(doc, `${monto < 0 ? '- ' : ''}${plata(Math.abs(monto))}`, ANCHO - M - 3, y + 3.5, { tam: F.texto, color: monto < 0 ? K.rojo : K.tinta, alinear: 'right' });
        y += 5.5;
    });
    if (filaTotal) {
        y += 1.5;
        doc.setFillColor(244, 244, 246);
        doc.roundedRect(M, y, CONTENT_W, altoCaja, 2, 2, 'F');
        doc.setFillColor(...K.rojo);
        doc.rect(M, y, 1.2, altoCaja, 'F');
        texto(doc, filaTotal[0], M + 5, y + 7.6, { tam: F.nombre, negrita: true });
        texto(doc, plata(filaTotal[1]), ANCHO - M - 4, y + 8.2, { tam: 16, negrita: true, alinear: 'right' });
        if (efectivo) {
            texto(doc, efectivo[0], M + 5, y + 14.6, { tam: F.texto, color: K.gris });
            texto(doc, plata(efectivo[1]), ANCHO - M - 4, y + 14.8, { tam: F.nombre, negrita: true, color: K.tinta, alinear: 'right' });
        }
        y += altoCaja + 2;
    }
    const nota = notas.filter(Boolean).join('  ·  ');
    if (nota) {
        doc.setFontSize(F.chico);
        const n = doc.splitTextToSize(nota, CONTENT_W);
        texto(doc, n, M, y + 1.5, { tam: F.chico, color: K.gris });
        y += n.length * 3.3 + 1.5;
    }
    return y + 4;
}

// ── Firmas: sobre una línea, como en papel ─────────────────────────────────────────────────
export function firmas(doc, ctx, y, { firmaCliente, firmaTecnico, aclaracionCliente, aclaracionTecnico, rolTecnico = 'Técnico' }) {
    if (!firmaCliente && !firmaTecnico) return y;
    const ALTO = 13;
    y = espacio(doc, ctx, y, ALTO + 13);
    const ancho = (CONTENT_W - 16) / 2;
    [[firmaCliente, aclaracionCliente, 'Firma del cliente'], [firmaTecnico, aclaracionTecnico, rolTecnico]].forEach(([img, acl, rol], i) => {
        const x = M + i * (ancho + 16);
        if (img) {
            try {
                const p = doc.getImageProperties(img);
                const r = Math.min((ancho * 0.8) / p.width, ALTO / p.height);
                doc.addImage(img, 'PNG', x + (ancho - p.width * r) / 2, y + (ALTO - p.height * r), p.width * r, p.height * r, undefined, 'FAST');
            } catch { /* firma rota */ }
        }
        linea(doc, y + ALTO + 1, { color: K.tinta, grosor: 0.25, x1: x, x2: x + ancho });
        if (acl) texto(doc, acl, x + ancho / 2, y + ALTO + 5.5, { tam: F.texto, negrita: true, alinear: 'center' });
        texto(doc, rol, x + ancho / 2, y + ALTO + (acl ? 9.3 : 5.5), { tam: F.chico, color: K.gris, alinear: 'center' });
    });
    return y + ALTO + 13;
}

// Datos del cliente para la columna izquierda
export function colCliente(cliente, sede) {
    const dir = sede?.direccion ? direccionConPisoTxt(sede) : (cliente?.direccion || null);
    const cond = condicionTexto(cliente?.condicionFiscal || cliente?.condicionIva);
    const sedeNom = sede?.nombreSede && !/^(principal|mostrador)$/i.test(String(sede.nombreSede).trim()) ? sede.nombreSede : null;
    return {
        rotulo: 'Cliente', nombre: cliente?.nombre || 'Cliente',
        lineas: [sedeNom, dir, [cliente?.telefono && `Tel. ${cliente.telefono}`, cond, cliente?.cuilDni && `CUIT/DNI ${cliente.cuilDni}`].filter(Boolean).join(' · ')],
    };
}
const CONDICIONES = {
    RESPONSABLE_INSCRIPTO: 'Responsable Inscripto', MONOTRIBUTO: 'Monotributo', EXENTO: 'IVA Exento',
    NO_RESPONSABLE: 'IVA No Responsable', CONSUMIDOR_FINAL: 'Consumidor final', SUJETO_NO_CATEGORIZADO: 'Sujeto no categorizado',
};
export const condicionTexto = (c) => CONDICIONES[String(c || '').toUpperCase()] || null;

// "Av. Cabildo 1117, CABA" + piso/depto cargados aparte → "Av. Cabildo 1117, Piso 3 Depto B, CABA"
function direccionConPisoTxt(sede) {
    const dir = String(sede?.direccion || '').trim();
    const extra = [sede?.piso && !/piso/i.test(dir) ? `Piso ${sede.piso}` : null,
        sede?.depto && !/(depto|dpto|departamento)/i.test(dir) ? `Depto ${sede.depto}` : null].filter(Boolean).join(' ');
    if (!extra) return dir;
    const coma = dir.lastIndexOf(',');
    return coma > 0 ? `${dir.slice(0, coma)}, ${extra}${dir.slice(coma)}` : `${dir}, ${extra}`;
}

// Línea de la empresa para el pie: lo arma layout.dibujarFooter; acá solo se exporta por si hace falta
export const empresa = () => getEmpresa();
